import { notificationFireTime } from './notificationScope'
import { Capacitor } from '@capacitor/core'

/**
 * Safe loader for @capacitor/local-notifications.
 *
 * CRITICAL ANTI-REGRESSION RULE:
 * Capacitor plugin Proxies MUST NEVER be returned directly from an async function
 * or as a Promise resolution value.
 *
 * Reason:
 * When an async function returns an object `x`, the JS engine executes the Promise
 * resolution procedure: `Resolve(p, x)`. If `typeof x.then === 'function'`, JS
 * assimilates `x` as a Thenable by invoking `x.then(resolve, reject)`.
 *
 * For Capacitor plugin Proxies, property access for unmapped properties triggers
 * dynamic native bridge dispatch. Accessing `LocalNotifications.then` returns a
 * callable wrapper that sends `LocalNotifications.then()` across the bridge to iOS.
 * Because iOS Capacitor has no `then` plugin method, it throws:
 *   [SU-Guide][JSRuntimeError] kind=unhandledrejection message="LocalNotifications.then()" is not implemented on ios
 *
 * Therefore, this helper returns a plain Module container object `{ LocalNotifications }`
 * (or null if non-native / failed), which has no `.then` property and is never assimilated.
 */

let testModuleOverride = null

export async function getLocalNotificationsModule() {
  if (testModuleOverride !== null) return testModuleOverride
  if (!Capacitor.isNativePlatform()) return null
  try {
    const mod = await import('@capacitor/local-notifications')
    // Return a plain container object so Promise assimilation does not touch LocalNotifications.then
    return { LocalNotifications: mod.LocalNotifications }
  } catch (err) {
    console.warn('[local-notif] plugin load failed', err)
    return null
  }
}

export async function scheduleLocalNotifications(plugin, notifications) {
  const fresh = () => notifications.filter(n => n.extra?.version !== 2 || notificationFireTime(n) > Date.now())
  if (Capacitor.getPlatform() !== 'android') {
    const current = fresh()
    return current.length ? plugin.schedule({ notifications: current }) : { notifications: [] }
  }
  let exactAllowed = false
  try {
    exactAllowed = (await plugin.checkExactNotificationSetting()).exact_alarm === 'granted'
  } catch { /* Keep automatic sync out of system settings when permission cannot be checked. */ }
  const current = fresh()
  if (!current.length) return { notifications: [] }
  return plugin.schedule({
    notifications: current.map(notification => ({
      ...notification,
      isExactNotification: notification.isExactNotification ?? (notification.isExactMandatory === true || exactAllowed),
    })),
  })
}

/**
 * Injects a mock module container for unit testing.
 * @param {{ LocalNotifications: any } | null} mockModule
 */
export function setLocalNotificationsModuleForTesting(mockModule) {
  testModuleOverride = mockModule ? { LocalNotifications: mockModule.LocalNotifications || mockModule } : null
}

export function resetLocalNotificationsModuleForTesting() {
  testModuleOverride = null
}
